import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
  TemplateRef,
  viewChild,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { AccessLevel, AccountMarketplace } from "@front/m19-api-client";
import { Marketplaces } from "@front/m19-models";
import { AccountMarketplaceService, AccountSelectionService } from "@front/m19-services";
import {
  IBadgeComponent,
  IButtonDirective,
  ICardComponent,
  ICardContent,
  ITooltipDirective,
  MarketplaceFlagComponent,
  MODAL_DATA,
  ModalRef,
  ModalService,
  SpinnerComponent,
  ToasterService,
} from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { catchError, EMPTY, filter, finalize, Observable, of, switchMap } from "rxjs";
import { LinkDspModalComponent, LinkDspModalData } from "./link-dsp/link-dsp.component";
import { take } from "rxjs/operators";

@Component({
  selector: "app-show-dsp",
  template: ` <ng-container *transloco="let t">
    <div class="flex flex-col gap-4">
      @if (loading()) {
        <div class="flex w-full items-center justify-center">
          <ISpinner [display]="true" />
        </div>
      } @else if (dspAdvertiser() === undefined) {
        <div class="p-2">
          <div class="border-l-4 border-orange-500 bg-orange-100 p-4 text-orange-700">
            {{ "Error retrieving DSP Advertisers." }}
          </div>
        </div>
      } @else {
        <div iCard class="max-w-[70vw]">
          <div iCardContent>
            <div class="mb-2 flex gap-3">
              <div class="text-main-500 font-semibold">{{ dspAdvertiser()!.name }}</div>
              <a [href]="dspAdvertiser()!.url" target="_blank" class="text-main-500 hover:text-main-600 truncate">{{
                dspAdvertiser()!.url
              }}</a>
            </div>
            <div class="flex gap-3">
              <IBadge
                color="gray"
                size="xs"
                label="ID: {{ dspAdvertiser()!.dspAdvertiserId }}"
                [tooltipValue]="t('show-dsp.dsp_advertiser_id_tooltip')"
              ></IBadge>
              @if (onM19Seat()) {
                <IBadge color="main" size="xs" [label]="t('common.m19_seat')"></IBadge>
              }
            </div>
          </div>
        </div>
      }
    </div>
    <div class="mt-4 flex items-center justify-end gap-2">
      @if (!loading()) {
        <button
          iButton
          variant="destructive"
          [loading]="isUnlinking()"
          [disabled]="modalData.disableLinkButton"
          [iTooltip]="modalData.disableLinkButton ? t('show-dsp.cannot_change_dsp_advertiser_link') : ''"
          (click)="unlink()"
        >
          {{ t("show-dsp.unlink") }}
        </button>
        <button
          iButton
          [disabled]="modalData.disableLinkButton || isUnlinking()"
          [iTooltip]="modalData.disableLinkButton ? t('show-dsp.cannot_change_dsp_advertiser_link') : ''"
          (click)="linkAnother()"
        >
          {{ t("show-dsp.link_another_dsp_advertiser") }}
        </button>
      }
      <button iButton variant="secondary" [disabled]="isUnlinking()" (click)="close()">
        {{ t("common.exit") }}
      </button>
    </div>
    <ng-template #confirmUnlinkModal>
      <div class="flex flex-col gap-2 p-4 text-sm text-gray-700">
        <div
          [innerHTML]="
            t('show-dsp.marketplaces_unlink_message', {
              accountId: modalData.accountMarketplace.accountId,
              dspAdvertiserName: dspAdvertiser()?.name,
            })
          "
        ></div>
        <div class="ml-2 flex flex-wrap items-center gap-3">
          @for (am of accountMarketplacesToUnlink() ?? []; track am.marketplace) {
            <div class="flex items-center gap-1">
              <IMarketplaceFlag [marketplace]="am.marketplace"></IMarketplaceFlag>
              <span>{{ am.marketplace }}</span>
            </div>
          }
        </div>
      </div>
      <div class="flex justify-end gap-4">
        <button iButton variant="secondary" (click)="confirmUnlinkModalRef?.close()">
          {{ t("common.cancel") }}
        </button>
        <button iButton variant="destructive" (click)="confirmUnlinkModalRef?.close(true)">
          {{ t("common.confirm") }}
        </button>
      </div>
    </ng-template>
  </ng-container>`,
  imports: [
    SpinnerComponent,
    ICardComponent,
    IBadgeComponent,
    IButtonDirective,
    ITooltipDirective,
    TranslocoDirective,
    ICardContent,
    MarketplaceFlagComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShowDspComponent {
  private readonly accountMarketplaceService = inject(AccountMarketplaceService);
  private readonly toastrService = inject(ToasterService);
  private readonly modalService = inject(ModalService);
  private readonly translocoService = inject(TranslocoService);
  private readonly accountSelectionService = inject(AccountSelectionService);
  readonly modalData = inject<ShowDspModalData>(MODAL_DATA);
  readonly modalRef = inject(ModalRef);

  confirmUnlinkModal = viewChild.required<TemplateRef<HTMLElement>>("confirmUnlinkModal");
  confirmUnlinkModalRef: ModalRef<boolean> | undefined;

  readonly Marketplaces = Marketplaces;
  readonly loading = signal(true);
  readonly dspAdvertiser = toSignal(
    this.accountMarketplaceService.getDspAdvertiser(this.modalData.dspAdvertiserId).pipe(
      take(1),
      catchError((error) => {
        this.toastrService.error(error, this.translocoService.translate("show-dsp.failed_to_load_dsp_advertiser"));
        return EMPTY;
      }),
      finalize(() => this.loading.set(false)),
    ),
  );
  readonly onM19Seat = computed(
    () => this.dspAdvertiser() && AccountMarketplaceService.isOnM19Seat(this.dspAdvertiser()!),
  );
  readonly isUnlinking = signal(false);

  selectedOrganizationAccountMarketplaces = toSignal(
    this.accountSelectionService.selectedOrganizationAccountMarketplaces$,
  );

  accountMarketplacesToUnlink = computed<AccountMarketplace[] | undefined>(() => {
    const dspAdvertiser = this.dspAdvertiser();
    if (!dspAdvertiser) return undefined;

    if (!dspAdvertiser.isRegional) return [this.modalData.accountMarketplace];

    return this.selectedOrganizationAccountMarketplaces()?.filter(
      (am) => am.dspAdvertiserId === dspAdvertiser.dspAdvertiserId && am.accessLevel === AccessLevel.ADMIN,
    );
  });

  unlink(): void {
    const accountMarketplacesToUnlink = this.accountMarketplacesToUnlink();
    if (this.modalData.disableLinkButton || !accountMarketplacesToUnlink) {
      return;
    }

    let maybeShowConfirmModal$: Observable<boolean> = of(true);
    if (accountMarketplacesToUnlink.length > 1) {
      this.confirmUnlinkModalRef = this.modalService.openModal<void, boolean>(this.confirmUnlinkModal(), {
        modalTitle: this.translocoService.translate("show-dsp.confirm_unlink"),
      });
      maybeShowConfirmModal$ = this.confirmUnlinkModalRef.afterClosed();
    }

    this.isUnlinking.set(true);

    maybeShowConfirmModal$
      .pipe(
        filter((confirmed) => confirmed),
        switchMap(() => this.accountMarketplaceService.linkDspAdvertiser(accountMarketplacesToUnlink, undefined)),
        finalize(() => this.isUnlinking.set(false)),
      )
      .subscribe({
        next: () => {
          this.toastrService.success(this.translocoService.translate("show-dsp.dsp_advertiser_unlinked_successfully"));
          this.modalRef.close();
        },
        error: (error) => {
          this.toastrService.error(error, this.translocoService.translate("show-dsp.failed_to_unlink_dsp_advertiser"));
        },
      });
  }

  linkAnother(): void {
    if (this.modalData.disableLinkButton) {
      return;
    }
    this.modalService.openModal<LinkDspModalData, void>(LinkDspModalComponent, {
      modalTitle: this.translocoService.translate("link-dsp.link_your_dsp_advertiser"),
      data: {
        accountMarketplace: this.modalData.accountMarketplace,
      },
    });
  }

  close(): void {
    this.modalRef.close();
  }
}

export interface ShowDspModalData {
  dspAdvertiserId: string;
  accountMarketplace: AccountMarketplace;
  disableLinkButton: boolean;
}
